import { MODE_3D_VIEW, SCENE_TONE_MAPPING_EXPOSURE } from '@constants'
import { refreshAllTextureAnisotropy } from '@utils/sceneObjects/textureCache'
import { isValidCameraView } from '@utils/camera/cameraView'
import { CubeCameraProvider } from '@context/CubeCameraContext'
import { Html } from '@react-three/drei'
import { useThree } from '@react-three/fiber'
import { useAppStore } from '@store'
import { useEffect, useState } from 'react'
import * as THREE from 'three'
import { Scene3D } from './Scene3D'

export function Viewer3D() {
  const { scene, controls, gl, camera } = useThree()
  const performanceMode = useAppStore((s) => s.performanceMode)
  const closeModal = useAppStore.use.closeModal()
  const idVersiones = useAppStore((s) => s.clientInfo.idVersiones)
  const idDisenos = useAppStore((store) => store.clientInfo.idDisenos)
  const [selector, setSelector] = useState(null)
  const [shouldInterval, setShouldInterval] = useState(true)
  const editWall = useAppStore.use.editWallProperty()
  // Lightweight performance mode: toggle renderer, lights and mesh shadow settings
  useEffect(() => {
    // 1) Renderer shadow config, DPR, and tone mapping for better visual quality
    if (gl) {
      ;(globalThis as any).__maxTextureAnisotropy = gl.capabilities.getMaxAnisotropy()
      gl.shadowMap.enabled = !performanceMode
      if (!performanceMode) {
        gl.shadowMap.type = THREE.PCFSoftShadowMap
      }
      gl.shadowMap.needsUpdate = true
      const newDPR = performanceMode ? 1 : window.devicePixelRatio
      gl.setPixelRatio(newDPR)
      
      // AgX conserva medios tonos y texturas sin quemar highlights como ACES
      gl.toneMapping = THREE.AgXToneMapping
      gl.toneMappingExposure = SCENE_TONE_MAPPING_EXPOSURE
      gl.outputColorSpace = THREE.SRGBColorSpace
      refreshAllTextureAnisotropy(scene)
    }

    // Helper to persist originals
    const ensureOriginal = (obj: any, key: string, value: any) => {
      if (!obj.userData) obj.userData = {}
      if (!obj.userData.__original) obj.userData.__original = {}
      if (obj.userData.__original[key] === undefined) obj.userData.__original[key] = value
    }

    scene.traverse((obj: any) => {
      // Lights: save and toggle shadow params
      if (obj.isLight) {
        ensureOriginal(obj, 'castShadow', obj.castShadow)
        if (obj.shadow) {
          if (!obj.shadow.userData) obj.shadow.userData = {}
          if (!obj.shadow.userData.__original) obj.shadow.userData.__original = {}
          if (obj.shadow.userData.__original.bias === undefined)
            obj.shadow.userData.__original.bias = obj.shadow.bias
          if (obj.shadow.userData.__original.radius === undefined)
            obj.shadow.userData.__original.radius = obj.shadow.radius
          const w = obj.shadow.mapSize?.width ?? 1024
          const h = obj.shadow.mapSize?.height ?? 1024
          if (obj.shadow.userData.__original.mapW === undefined)
            obj.shadow.userData.__original.mapW = w
          if (obj.shadow.userData.__original.mapH === undefined)
            obj.shadow.userData.__original.mapH = h
          if (obj.shadow.camera) {
            if (!obj.shadow.camera.userData) obj.shadow.camera.userData = {}
            if (!obj.shadow.camera.userData.__original) obj.shadow.camera.userData.__original = {}
            if (obj.shadow.camera.userData.__original.near === undefined)
              obj.shadow.camera.userData.__original.near = obj.shadow.camera.near
            if (obj.shadow.camera.userData.__original.far === undefined)
              obj.shadow.camera.userData.__original.far = obj.shadow.camera.far
          }
        }

        if (performanceMode) {
          obj.castShadow = false
        } else {
          obj.castShadow = obj.userData.__original?.castShadow ?? true
          if (obj.shadow) {
            const o = obj.shadow.userData?.__original
            if (o) {
              obj.shadow.bias = o.bias
              obj.shadow.radius = o.radius
              if (o.mapW && o.mapH) obj.shadow.mapSize.set(o.mapW, o.mapH)
            }
            if (obj.shadow.camera) {
              // Fallback to camera originals stored earlier
              const on = obj.shadow.camera.userData?.__original?.near
              const of = obj.shadow.camera.userData?.__original?.far
              if (on !== undefined) obj.shadow.camera.near = on
              if (of !== undefined) obj.shadow.camera.far = of
              obj.shadow.camera.updateProjectionMatrix?.()
            }
          }
        }
      }

      // Meshes: save and toggle
      if (obj.isMesh) {
        // Save only once
        ensureOriginal(obj, 'castShadow', obj.castShadow)
        ensureOriginal(obj, 'receiveShadow', obj.receiveShadow)

        if (performanceMode) {
          obj.castShadow = false
          obj.receiveShadow = false
        } else {
          obj.castShadow = obj.userData.__original?.castShadow ?? true
          obj.receiveShadow = obj.userData.__original?.receiveShadow ?? true
        }

        // Force material update
        if (obj.material) {
          const mats = Array.isArray(obj.material) ? obj.material : [obj.material]
          mats.forEach((m) => (m.needsUpdate = true))
        }
      }
    })
  }, [performanceMode, scene, gl])
  // console.log('validando conexión', connection)
  // useEffect(() => {
  //   // Reducir la resolución de la cámara
  //   gl.setPixelRatio(window.devicePixelRatio / 2)
  //   // Reducir la calidad de las texturas
  //   scene.traverse((object) => {
  //     if (object.isMesh && object.material.map) {
  //       // object.material.map.minFilter = THREE.LinearFilter
  //       object.material.map.generateMipmaps = false
  //     }
  //   })
  // }, [gl, scene])

  const mode = useAppStore((s) => s.mode)
  const lastView = useAppStore((s) => s.lastView)
  const shouldFitCameraToItems = useAppStore.use.shouldFitCameraToItems()
  const unitReplaceInFlight = useAppStore.use.unitReplaceInFlight()
  const cameraViewPreservationDepth = useAppStore.use.cameraViewPreservationDepth()
  const items = useAppStore.use.items()
  // Verificar si hay items en la escena para no resetear la cámara cuando hay items
  const hasItems = Array.from(items.values()).some(
    (item) =>
      (item.type === 'items' || item.type === 'linkedItem') &&
      item.matrix &&
      item.width &&
      item.height,
  )

  //TODO: Revisar este useEffect mas adelante porque se puede hacer algo mejor
  useEffect(() => {
    if (!controls || shouldFitCameraToItems || mode !== MODE_3D_VIEW) return
    if (unitReplaceInFlight || cameraViewPreservationDepth > 0) return
    if (lastView && isValidCameraView(lastView)) return
    if (hasItems) return

    controls.setPosition(0, 1500, -2500)
    controls.moveTo(0, 1500, 0)
    controls.setPosition(0, 1500, -2500)
    controls.moveTo(0, 1500, 0)
  }, [
    controls,
    shouldFitCameraToItems,
    unitReplaceInFlight,
    cameraViewPreservationDepth,
    hasItems,
    items,
    mode,
    lastView,
  ])

  async function prepareSceneForExport(sceneToExport: THREE.Scene) {
    // console.log('prepareSceneForExport called with:', {
    //   sceneToExport,
    //   isScene: sceneToExport instanceof THREE.Scene,
    //   hasTraverse: sceneToExport && typeof sceneToExport.traverse === 'function',
    // })

    if (!sceneToExport) {
      console.error('Error: sceneToExport is undefined')
      throw new Error('Scene is undefined')
    }

    if (!(sceneToExport instanceof THREE.Scene)) {
      console.error('Error: sceneToExport is not a THREE.Scene instance', {
        sceneToExport,
        type: typeof sceneToExport,
        constructor: sceneToExport?.constructor?.name,
      })
      throw new Error('Scene is not a valid THREE.Scene instance')
    }

    if (typeof sceneToExport.traverse !== 'function') {
      console.error('Error: sceneToExport.traverse is not a function', {
        sceneToExport,
        traverse: sceneToExport.traverse,
        type: typeof sceneToExport.traverse,
      })
      throw new Error('Scene traverse is not a function')
    }

    // Validar que gl y camera estén disponibles
    if (!gl) {
      console.error('Error: gl is undefined')
      throw new Error('GL renderer is undefined')
    }

    if (!camera) {
      console.error('Error: camera is undefined')
      throw new Error('Camera is undefined')
    }

    const originalItems = useAppStore.getState().items
    const sceneBoundingBoxes = useAppStore.getState().sceneBoundingBoxes
    const originalPrefs = useAppStore.getState().prefs
    useAppStore.setState({
      prefs: {
        ...originalPrefs,
        VERCOTAVENTPRTAS3D: false,
        VERCOTAMUEBLESALTOS3: false,
        VERCOTAMUEBLESBAJOS3: false,
        VERUNIDADESENCOTAS3D: false,
        VERCOTACONSTRUCCION3: false,

        VERCOTACONSTRUCCION: false,
        VERCOTAMUEBLESALTOS: false,
        VERCOTAMUEBLESBAJOS: false,
        VERCOTATABIQUES: false,
        VERCOTAVENTPRTAS: false,
        VERUNIDADESENCOTAS: false,

        VERCOTACONSTRUCC_ALZ: false,
        VERCOTAALTOSALZ: false,
        VERCOTABAJOSALZ: false,
      },
    })
    gl.render(sceneToExport, camera)
    if (sceneBoundingBoxes) {
      for (const [OBBID] of sceneBoundingBoxes.entries()) {
        if (OBBID.startsWith('COBB_')) continue
        editWall(OBBID, { visible: true, wallVisible: true })
      }
    }

    // Preparar luces para exportación GLTF
    const lightModifications: Array<{
      light: THREE.Light
      originalVisible?: boolean
      originalDecay?: number
      targetChild?: THREE.Object3D
      originalTarget?: THREE.Object3D
      originalTargetParent?: THREE.Object3D | null
      originalTargetPosition?: THREE.Vector3 | null
      originalTargetWorldPosition?: THREE.Vector3 | null
      originalTargetLocalPosition?: THREE.Vector3 | null
    }> = []

    // Validar scene nuevamente antes de traverse (usando sceneToExport)
    // Guardar una referencia local para evitar problemas de scope
    const sceneRef = sceneToExport
    if (!sceneRef) {
      console.error('Error: sceneRef is undefined before traverse')
      throw new Error('Scene reference is undefined before traverse')
    }

    if (!(sceneRef instanceof THREE.Scene)) {
      console.error('Error: sceneRef is not a THREE.Scene instance', { sceneRef })
      throw new Error('Scene reference is not a valid THREE.Scene')
    }

    if (typeof sceneRef.traverse !== 'function') {
      console.error('Error: sceneRef.traverse is not a function', {
        sceneRef,
        type: typeof sceneRef,
        hasTraverse: 'traverse' in sceneRef,
      })
      throw new Error('Scene traverse is not a function')
    }

    // Limpiar hijos undefined/null antes de hacer traverse
    const cleanScene = (obj: THREE.Object3D) => {
      if (!obj) return

      // Filtrar hijos undefined/null
      const validChildren: THREE.Object3D[] = []
      for (let i = 0; i < obj.children.length; i++) {
        const child = obj.children[i]
        if (child && child instanceof THREE.Object3D) {
          validChildren.push(child)
          // Limpiar recursivamente
          cleanScene(child)
        } else {
          console.warn(
            `Warning: hijo inválido encontrado en ${obj.name || obj.type}, removiendo...`,
            { child },
          )
        }
      }

      // Reemplazar hijos con solo los válidos
      if (validChildren.length !== obj.children.length) {
        obj.children = validChildren
      }
    }

    // Limpiar la escena antes de hacer traverse
    try {
      cleanScene(sceneRef)
    } catch (cleanError) {
      console.warn('Warning: error al limpiar escena, continuando...', cleanError)
    }

    try {
      // Limpiar hijos undefined antes de usar traverse nativo
      const cleanChildren = (obj: THREE.Object3D) => {
        if (!obj || !obj.children) return

        const validChildren: THREE.Object3D[] = []
        for (let i = 0; i < obj.children.length; i++) {
          const child = obj.children[i]
          if (child && child instanceof THREE.Object3D && child.isObject3D) {
            validChildren.push(child)
            cleanChildren(child) // Limpiar recursivamente
          } else {
            console.warn(`Hijo inválido encontrado en ${obj.name || obj.type}, removiendo...`, {
              child,
            })
          }
        }

        // Reemplazar hijos con solo los válidos
        if (validChildren.length !== obj.children.length) {
          obj.children = validChildren
        }
      }

      // Limpiar la escena antes de hacer traverse
      try {
        cleanChildren(sceneRef)
      } catch (cleanError) {
        console.warn('Warning: error al limpiar escena, continuando...', cleanError)
      }

      // Validar que sceneRef todavía sea válido después de limpiar
      if (!sceneRef || !sceneRef.traverse) {
        throw new Error('Scene reference is invalid after cleaning')
      }

      // Función recursiva segura que evita el problema de hijos undefined
      const processLights = (obj: THREE.Object3D) => {
        if (!obj || !(obj instanceof THREE.Object3D)) return

        try {
          // Procesar el objeto actual si es una luz
          if (obj instanceof THREE.Light) {
            processLightObject(obj)
          }

          // Procesar hijos recursivamente
          const children = Array.from(obj.children)
          for (const child of children) {
            if (child && child instanceof THREE.Object3D) {
              processLights(child)
            }
          }
        } catch (err) {
          console.warn(`Error procesando objeto ${obj.name || obj.type}:`, err)
        }
      }

      const processLightObject = (object: THREE.Light) => {
        // Validar que el objeto no sea undefined o null
        if (!object) {
          console.warn('Warning: encontrado objeto undefined/null durante traverse, saltando...')
          return
        }

        // Ocultar AmbientLight ya que no es soportado por GLTF
        if (object instanceof THREE.AmbientLight) {
          lightModifications.push({ light: object, originalVisible: object.visible })
          object.visible = false
        }

        // Configurar DirectionalLight y SpotLight para exportación correcta
        // Según las advertencias del GLTFExporter, el target debe ser hijo de la luz con posición (0,0,-1)
        if (object instanceof THREE.DirectionalLight || object instanceof THREE.SpotLight) {
          // Validar que la luz tenga una posición válida
          const lightPos = object.position
          if (
            isNaN(lightPos.x) ||
            isNaN(lightPos.y) ||
            isNaN(lightPos.z) ||
            !isFinite(lightPos.x) ||
            !isFinite(lightPos.y) ||
            !isFinite(lightPos.z)
          ) {
            console.warn(
              `Warning: Luz ${object.name || object.uuid} tiene posición inválida (NaN o Infinity), saltando...`,
              { position: lightPos },
            )
            return
          }

          // Guardar la posición original del target antes de modificarlo
          // IMPORTANTE: Para SpotLight, el target está dentro de LightPivot (Group), así que necesitamos
          // guardar la posición relativa al parent original (Group), no a la luz
          const originalTargetPosition = object.target ? object.target.position.clone() : null
          const originalTargetParent = object.target?.parent || null
          let originalTargetWorldPosition: THREE.Vector3 | null = null
          let originalTargetLocalPosition: THREE.Vector3 | null = null

          if (object.target) {
            try {
              // Obtener posición mundial del target ANTES de moverlo
              originalTargetWorldPosition = object.target.getWorldPosition(new THREE.Vector3())
              // Validar que la posición mundial no tenga NaN
              if (
                isNaN(originalTargetWorldPosition.x) ||
                isNaN(originalTargetWorldPosition.y) ||
                isNaN(originalTargetWorldPosition.z)
              ) {
                console.warn(
                  `Warning: Target de luz ${object.name || object.uuid} tiene posición mundial inválida, usando posición relativa`,
                )
                originalTargetWorldPosition = null
              }

              // CRÍTICO: Si el target está en un parent diferente a la luz (como LightPivot/Group),
              // la posición relativa debe ser respecto al parent original, no a la luz
              // originalTargetPosition ya tiene la posición relativa al parent original
              if (
                originalTargetPosition &&
                originalTargetParent &&
                originalTargetParent !== object
              ) {
                // Usar la posición relativa al parent original directamente
                originalTargetLocalPosition = originalTargetPosition.clone()
              } else {
                // Si el target está directamente en la luz, calcular posición relativa a la luz
                const lightWorldPos = object.getWorldPosition(new THREE.Vector3())
                originalTargetLocalPosition = new THREE.Vector3().subVectors(
                  originalTargetWorldPosition || originalTargetPosition || new THREE.Vector3(),
                  lightWorldPos,
                )
              }

              // console.log('🔧 prepareSceneForExport - Guardando target:', {
              //   id: object.name,
              //   originalTargetPosition: originalTargetPosition ? `[${originalTargetPosition.x.toFixed(2)}, ${originalTargetPosition.y.toFixed(2)}, ${originalTargetPosition.z.toFixed(2)}]` : 'N/A',
              //   originalTargetParent: originalTargetParent?.type || originalTargetParent?.constructor?.name || 'null',
              //   originalTargetWorldPosition: originalTargetWorldPosition ? `[${originalTargetWorldPosition.x.toFixed(2)}, ${originalTargetWorldPosition.y.toFixed(2)}, ${originalTargetWorldPosition.z.toFixed(2)}]` : 'N/A',
              //   originalTargetLocalPosition: originalTargetLocalPosition ? `[${originalTargetLocalPosition.x.toFixed(2)}, ${originalTargetLocalPosition.y.toFixed(2)}, ${originalTargetLocalPosition.z.toFixed(2)}]` : 'N/A',
              // })
            } catch (error) {
              console.warn(
                `Warning: Error al obtener posición del target de luz ${object.name || object.uuid}:`,
                error,
              )
              originalTargetWorldPosition = null
              originalTargetLocalPosition = null
            }
          }

          // Si el target no es hijo de la luz, moverlo para que sea hijo
          if (object.target && object.target.parent !== object) {
            // Remover el target de su parent actual
            if (originalTargetParent) {
              originalTargetParent.remove(object.target)
            }
            // Agregar el target como hijo de la luz
            object.add(object.target)
            // Establecer posición relativa (0, 0, -1) según recomendación GLTF
            object.target.position.set(0, 0, -1)
            lightModifications.push({
              light: object,
              originalTarget: object.target,
              originalTargetParent: originalTargetParent,
              originalTargetPosition: originalTargetPosition,
              originalTargetWorldPosition: originalTargetWorldPosition,
              originalTargetLocalPosition: originalTargetLocalPosition,
            })
          } else if (object.target && object.target.parent === object) {
            // Si ya es hijo, guardar la posición original y establecer (0, 0, -1)
            const originalPos = object.target.position.clone()
            // Validar que la posición original no tenga NaN
            if (
              !isNaN(originalPos.x) &&
              !isNaN(originalPos.y) &&
              !isNaN(originalPos.z) &&
              isFinite(originalPos.x) &&
              isFinite(originalPos.y) &&
              isFinite(originalPos.z)
            ) {
              object.target.position.set(0, 0, -1)
              lightModifications.push({
                light: object,
                originalTarget: object.target,
                originalTargetPosition: originalPos,
                originalTargetWorldPosition: originalTargetWorldPosition,
                originalTargetLocalPosition: originalTargetLocalPosition || originalPos,
              })
            } else {
              console.warn(
                `Warning: Target de luz ${object.name || object.uuid} tiene posición original inválida, usando posición por defecto`,
              )
              object.target.position.set(0, 0, -1)
              lightModifications.push({
                light: object,
                originalTarget: object.target,
                originalTargetPosition: new THREE.Vector3(0, 0, -1),
                originalTargetWorldPosition: originalTargetWorldPosition,
                originalTargetLocalPosition: new THREE.Vector3(0, 0, -1),
              })
            }
          } else if (!object.target) {
            // Si no hay target, crear uno
            const newTarget = new THREE.Object3D()
            newTarget.position.set(0, 0, -1)
            object.add(newTarget)
            object.target = newTarget
            lightModifications.push({ light: object, targetChild: newTarget })
          }
        }

        // Configurar decay para SpotLight (glTF requiere decay=2 según advertencia del exporter)
        if (object instanceof THREE.SpotLight) {
          lightModifications.push({ light: object, originalDecay: object.decay })
          object.decay = 2
        }

        // También configurar decay para PointLight si es necesario
        if (object instanceof THREE.PointLight && object.decay !== 2) {
          lightModifications.push({ light: object, originalDecay: object.decay })
          object.decay = 2
        }
      }

      // Llamar a processLights en lugar de usar traverse nativo
      processLights(sceneRef)
    } catch (error) {
      console.error('Error during scene processing:', error)
      throw error
    }

    return { scene: sceneToExport, originalItems, originalPrefs, lightModifications }
  }

  useEffect(() => {
    if (!shouldInterval) return
    const interval = setInterval(() => {
      if (document?.querySelector('#ontoiucame')) {
        setSelector(document?.querySelector('#ontoiucame'))
        setShouldInterval(false)
        clearInterval(interval)
      }
    }, 100)
    return () => clearInterval(interval)
  }, [Boolean(selector)])

  return (
    <>
      <Html
        transform
        occlude
      ></Html>
      <CubeCameraProvider>
        <Scene3D />
      </CubeCameraProvider>
    </>
  )
}
